import { useEffect, useState } from "react";

export type Breakpoints = Record<string, number>;

export default function breakpointsFactory<T extends Breakpoints>(
	breakpoints: T,
) {
	type BreakpointKey = keyof T;

	const useMediaQuery = (query: string): boolean => {
		const [matches, setMatches] = useState<boolean>(() => {
			if (typeof window !== "undefined") {
				return window.matchMedia(query).matches;
			}
			return false;
		});

		useEffect(() => {
			const mql = window.matchMedia(query);
			const handler = (e: MediaQueryListEvent) =>
				window.requestAnimationFrame(() =>
					window.requestAnimationFrame(() => setMatches(e.matches)),
				);
			mql.addEventListener("change", handler);
			return () => {
				mql.removeEventListener("change", handler);
			};
		}, [query]);

		return matches;
	};

	const createQueryIsBelow = (breakpoint: BreakpointKey | number) => {
		const value =
			typeof breakpoint === "number" ? breakpoint : breakpoints[breakpoint];
		return `(max-width: ${value - 1}px)`;
	};

	const createQueryIsAbove = (breakpoint: BreakpointKey | number) => {
		const value =
			typeof breakpoint === "number" ? breakpoint : breakpoints[breakpoint];
		return `(min-width: ${value}px)`;
	};

	const createQueryIsBetween = (
		low: BreakpointKey | number,
		high: BreakpointKey | number,
	) => {
		const lowValue = typeof low === "number" ? low : breakpoints[low];
		const highValue = typeof high === "number" ? high : breakpoints[high];
		return `(min-width: ${lowValue}px) and (max-width: ${highValue - 1}px)`;
	};

	const createQueryIsBelowHeight = (breakpoint: BreakpointKey | number) => {
		const value =
			typeof breakpoint === "number" ? breakpoint : breakpoints[breakpoint];
		return `(max-height: ${value - 1}px)`;
	};

	const useIsBelow = (breakpoint: BreakpointKey) =>
		useMediaQuery(createQueryIsBelow(breakpoint));

	const useIsAbove = (breakpoint: BreakpointKey) =>
		useMediaQuery(createQueryIsAbove(breakpoint));

	const useBelowHeight = (breakpoint: BreakpointKey) =>
		useMediaQuery(createQueryIsBelowHeight(breakpoint));

	return {
		useMediaQuery,
		useIsBelow,
		useIsAbove,
		useBelowHeight,
		createQueryIsAbove,
		createQueryIsBelow,
		createQueryIsBetween,
		createQueryIsBelowHeight,
	};
}
